import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import React, { useCallback, useState } from 'react';
import type { Asset, WorkOrderTemplate } from '#/graphql/types';
import If from '#/common/components/If';
import styled from 'styled-components';
import { List as DxList } from 'devextreme-react/list';
import { ClickEvent } from 'devextreme/ui/button';
import { color } from '@nuvolo/nuux-theme/utils';
import StandardThrobber from '#/app/components/Throbbers/StandardThrobber';
import FAIcon from '#/common/components/FAIcon';

export interface TemplatesListProps {
  loading: boolean;
  asset: Asset | undefined;
  templates: WorkOrderTemplate[] | undefined;
  previewTemplate?: WorkOrderTemplate;
  onSelect: (templates: WorkOrderTemplate[]) => void;
  onSelectToPreview: (template: WorkOrderTemplate) => void;
}

const StyledList = styled(DxList)`
  .dx-item.dx-list-item {
    .dx-list-item-content {
      padding: 0;
    }

    &.dx-state-hover {
      &:has(.preview-btn-container:hover) {
        background-color: transparent !important;
      }
      .preview-btn-container:not(:hover) {
        background-color: ${color('contrast', 'light')};
      }
    }

    .preview-btn-container {
      padding: 10px;
      margin-left: auto;
      display: flex;
      align-items: center;
      align-content: center;
      align-self: center;

      &:hover {
        background-color: ${color('primary', 'light3')};
      }
    }
  }
  .item-container {
    background-color: transparent !important;
    border-color: transparent !important;
  }
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  align-self: center;
`;

const StyledIconButton = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  align-content: center;
  align-self: center;
  height: 32px;
  width: 32px;
  padding: 6px;
  border: 2px solid transparent;

  &.active {
    border: 2px solid ${color('custom', 'blueAccent')};
    color: ${color('custom', 'blueAccent')};
    background: ${color('custom', 'blueBackground')};
    border-radius: 4px;
  }
`;

const ListRow = ({ item, click, preview }: any) => (
  <StyledRow className="dx-list-item-content">
    <Title className="dx-list-item-text">{item?.title || ''}</Title>
    <div className="preview-btn-container" onClick={click}>
      <StyledIconButton className={['preview-btn', preview ? 'active' : 'normal'].join(' ')}>
        <FAIcon icon="eye" />
      </StyledIconButton>
    </div>
  </StyledRow>
);

const TemplatesList: React.FC<TemplatesListProps> = ({
  loading,
  asset,
  templates,
  onSelect,
  onSelectToPreview,
  previewTemplate,
}) => {
  const { height, size } = useViewport();
  const listHeight = Math.max(386, size === 'xs' ? height - 630 : height - 590);

  const [selectedWoTemplate, setSelectedWoTemplate] = useState([] as WorkOrderTemplate[]);

  const onTemplateSelect = useCallback((t: WorkOrderTemplate[]) => {
    setSelectedWoTemplate(t);
  }, []);

  const handleClick = useCallback(
    (e: any, t: WorkOrderTemplate) => {
      e.stopPropagation();
      e.preventDefault();
      onSelectToPreview(t);
    },
    [onSelectToPreview],
  );

  const handleSelectionChanged = useCallback(
    (e: any) => {
      if (!e?.component?.option('selectedItems')) {
        return;
      }

      const selected = e?.component?.option('selectedItems') as WorkOrderTemplate[];

      onSelect(selected);
    },
    [onSelect],
  );

  return (
    <>
      <If condition={loading}>
        <StandardThrobber />
      </If>
      <If condition={!loading}>
        <StyledList
          height={listHeight}
          dataSource={templates}
          selectionMode="all"
          selectAllText={`Select All (${selectedWoTemplate?.length || 0})`}
          onSelectedItemsChange={onTemplateSelect}
          noDataText={asset ? 'No templates found' : 'Select an asset first'}
          showSelectionControls
          focusStateEnabled={false}
          activeStateEnabled={false}
          onSelectionChanged={handleSelectionChanged}
          itemRender={(item) => (
            <ListRow
              item={item}
              click={(e: ClickEvent) => handleClick(e, item)}
              preview={previewTemplate?.id === item.id}
            />
          )}
        />
      </If>
    </>
  );
};

export default React.memo(TemplatesList);
